import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  MdCalendarMonth,
  MdCalendarViewMonth,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import { selectSiteTimeZone } from '../../views/settings/reducer';
import { useIntl } from 'react-intl';

/**
 * Generic event type for the calendar
 * @date {Date} Date of the event
 * @data {any} Generic data object that can be consumed by the render function
 */
export interface CalendarEvent {
  date: Date;
  data?: any;
}

interface CalendarDayData {
  date: Date;
  events?: CalendarEvent[];
}

interface CalendarDayProps {
  /** Date of the day */
  date: Date;
  /** Date key for selection lookup */
  dateKey: string;
  /** Optional list of events */
  events?: CalendarEvent[];
  /** Optional render function for each event */
  renderEvent?: any;
  /** Is this day today? */
  isToday?: boolean;
  /** Is this day selected? */
  isSelected?: boolean;
  /** Optional onClick handler for day */
  onClick?: any;
  /** Optional pointer handlers for touch drag selection */
  onPointerDown?: any;
  onPointerEnter?: any;
  onPointerUp?: any;
}

export const CalendarDay = ({
  date,
  dateKey,
  events,
  renderEvent,
  isToday = false,
  isSelected = false,
  onClick,
  onPointerDown,
  onPointerEnter,
  onPointerUp,
}: CalendarDayProps) => {
  const eventLimit = 3;
  const handleClick = () => {
    if (onClick) onClick();
  };
  return (
    <div
      className={`day${isToday ? ' is-today' : ''}${isSelected ? ' selected-day' : ''}`}
      data-date={dateKey}
      onClick={handleClick}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerUp={onPointerUp}
    >
      <span className="date-number">{date.getDate()}</span>
      <div className="events">
        {events &&
          renderEvent &&
          events
            .slice(0, eventLimit)
            .map((event: CalendarEvent, i: number) => (
              <React.Fragment key={i}>{renderEvent(event)}</React.Fragment>
            ))}
      </div>
      {events && events.length > eventLimit && (
        <span className="additional-events">{`+${events.length - eventLimit} more`}</span>
      )}
    </div>
  );
};

interface CalendarProps {
  /** List of events that should be displayed */
  events?: CalendarEvent[];
  /** Render function for each event (passed to CalendarDay component) */
  renderEvent?: any;
  /** Initial view mode of the calendar (default is monthly) */
  initialView?: CalendarViewTypes;
  /** Optional selected dates (for multi-select) */
  selectedDates?: Date[];
  /** Optional date click handler */
  onSelectDate?: (date: Date) => void;
  /** Optional range selection handler (for touch drag select) */
  onSelectRange?: (dates: Date[]) => void;
  /** Optional callback for view changes */
  onViewShift?: (dates: Date[]) => void;
}

export enum CalendarViewTypes {
  Monthly,
  BiWeekly,
  Weekly,
  MonthAhead,
}

export const Calendar = ({
  initialView = CalendarViewTypes.Monthly,
  events,
  renderEvent,
  selectedDates,
  onSelectDate,
  onSelectRange,
  onViewShift,
}: CalendarProps) => {
  const timezone = useSelector(selectSiteTimeZone);
  const today = new Date();
  const [currentDate, setCurrentDate] = useState<Date>(today);
  const [view, setView] = useState(initialView);
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [calendarDays, setCalendarDays] = useState<CalendarDayData[]>([]);
  const [calendarTitle, setCalendarTitle] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<Date | null>(null);
  const isDraggingRef = useRef(false);
  const didDragRef = useRef(false);
  const suppressClickRef = useRef(false);
  const lastDragDateRef = useRef<Date | null>(null);
  const intl = useIntl();

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const convertTZ = (date: Date | string, tzString: string) => {
    return new Date(
      (typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', {
        timeZone: tzString,
      })
    );
  };

  const weekCount = (year: number, month: number) => {
    // month is in the range 1..12
    const firstOfMonth = new Date(year, month - 1, 1);
    const lastOfMonth = new Date(year, month, 0);
    const used = firstOfMonth.getDay() + lastOfMonth.getDate();
    return Math.ceil(used / 7);
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const toDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const getDateRange = (start: Date, end: Date) => {
    const startDay = toDay(start);
    const endDay = toDay(end);
    const range: Date[] = [];
    const step = startDay <= endDay ? 1 : -1;
    const current = new Date(startDay);
    while (step === 1 ? current <= endDay : current >= endDay) {
      range.push(new Date(current));
      current.setDate(current.getDate() + step);
    }
    return range;
  };

  const isIpadTouch = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /iPad/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
  };

  const canDragSelect = (event: React.PointerEvent) => {
    if (!onSelectRange) return false;
    if (event.pointerType === 'touch') return true;
    if (isIpadTouch()) return true;
    return false;
  };

  const isSelectedDay = (date: Date) => {
    if (selectedDates) {
      return selectedDates.some((d) => isSameDay(d, date));
    }
    return isSameDay(date, selectedDate);
  };

  const calendarDayString = (date: Date) => {
    return `${months[date.getMonth()].substring(0, 3)} ${date.getDate()}`;
  };

  const getCalendarTitle = () => {
    if (view == CalendarViewTypes.Monthly) {
      return months[currentDate.getMonth()];
    } else {
      const firstDay = calendarDays[0].date;
      const lastDay = calendarDays[calendarDays.length - 1].date;
      return `${calendarDayString(firstDay)} - ${calendarDayString(lastDay)}`;
    }
  };

  const eventsForDate = (date: Date, eventList: CalendarEvent[]) => {
    const tomorrow = new Date(date);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return eventList.filter((event: CalendarEvent) => {
      return event.date >= date && event.date < tomorrow;
    });
  };

  const getWeekDays = (date: Date) => {
    const dayOfWeek = date.getDay();

    // Calculate the date of the Sundy of the current week
    const startOfWeek = new Date(date); // Clone the date object
    startOfWeek.setDate(date.getDate() - dayOfWeek); // Set to the preceding Sunday

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const current = new Date(startOfWeek);
      current.setDate(startOfWeek.getDate() + i);
      let filteredEvents;
      if (events) {
        filteredEvents = eventsForDate(current, events);
      }
      weekDates.push({ date: current, events: filteredEvents });
    }
    return weekDates;
  };

  const getCalendarMonth = (date: Date) => {
    const startingDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const weeks = weekCount(date.getFullYear(), date.getMonth() + 1);
    const days = [];
    const nextWeek = new Date(startingDay);
    for (let i = 0; i < weeks; i++) {
      days.push(...getWeekDays(nextWeek));
      nextWeek.setDate(nextWeek.getDate() + 7);
    }
    return days;
  };

  const getCalendarWeeks = (date: Date, weeks = 1) => {
    const days = [...getWeekDays(date)];
    const anotherWeek = new Date(date);
    for (let i = 1; i < weeks; i++) {
      anotherWeek.setDate(anotherWeek.getDate() + 7);
      days.push(...getWeekDays(anotherWeek));
    }
    return days;
  };

  const addMonth = (date: Date) => {
    let current;
    if (date.getMonth() == 11) {
      current = new Date(date.getFullYear() + 1, 0, 1);
    } else {
      current = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    }
    return current;
  };

  const subtractMonth = (date: Date) => {
    let current;
    if (date.getMonth() == 0) {
      current = new Date(date.getFullYear() - 1, 11, 1);
    } else {
      current = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    }
    return current;
  };

  const increment = () => {
    if (view == CalendarViewTypes.Monthly) {
      setCurrentDate(addMonth(currentDate));
    } else {
      const newDate = new Date(currentDate);
      let addedDays = 7;
      if (view == CalendarViewTypes.BiWeekly) {
        addedDays = 14;
      }
      newDate.setDate(currentDate.getDate() + addedDays);
      setCurrentDate(newDate);
    }
  };

  const decrement = () => {
    if (view == CalendarViewTypes.Monthly) {
      setCurrentDate(subtractMonth(currentDate));
    } else {
      const newDate = new Date(currentDate);
      let subtractedDays = 7;
      if (view == CalendarViewTypes.BiWeekly) {
        subtractedDays = 14;
      }
      newDate.setDate(currentDate.getDate() - subtractedDays);
      setCurrentDate(newDate);
    }
  };

  useEffect(() => {
    const setSelected = isSameDay(currentDate, selectedDate);
    const convertedCurrent = convertTZ(currentDate, timezone);
    // After accounting for timezone we reset time on the day
    convertedCurrent.setHours(0, 0, 0, 0);
    setCurrentDate(convertedCurrent);
    if (setSelected) {
      setSelectedDate(convertedCurrent);
    }
  }, [timezone]);

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  useEffect(() => {
    if (view == CalendarViewTypes.Monthly) {
      setCalendarDays(getCalendarMonth(currentDate));
    } else {
      if (view == CalendarViewTypes.BiWeekly) {
        setCalendarDays(getCalendarWeeks(currentDate, 2));
      } else if (view == CalendarViewTypes.MonthAhead) {
        setCalendarDays(getCalendarWeeks(currentDate, 4));
      } else {
        setCalendarDays(getCalendarWeeks(currentDate));
      }
    }
  }, [currentDate, view, events]);

  useEffect(() => {
    if (calendarDays.length) {
      setCalendarTitle(getCalendarTitle());
      if (onViewShift) onViewShift(calendarDays.map((cd: CalendarDayData) => cd.date));
    }
  }, [calendarDays]);

  useEffect(() => {
    if (!isDragging) return;
    const handlePointerUp = () => {
      if (!isDraggingRef.current) return;
      suppressClickRef.current = didDragRef.current;
      isDraggingRef.current = false;
      dragStartRef.current = null;
      didDragRef.current = false;
      lastDragDateRef.current = null;
      setIsDragging(false);
      if (suppressClickRef.current) {
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
      }
    };
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging]);

  return (
    <div className="calendar">
      <div className="calendar-heading">
        <span className="calendar-month">
          <span className="calendar-title">{calendarTitle}</span>
        </span>
        <span className="calendar-controls">
          <div>
            <button
              className="button transparent"
              onClick={() => decrement()}
              title={intl.formatMessage({ id: 'previous' })}
            >
              <MdChevronLeft />
            </button>
            <button
              className="button transparent"
              onClick={() => increment()}
              title={intl.formatMessage({ id: 'next' })}
            >
              <MdChevronRight />
            </button>
          </div>
          <div className="calendar-viewpicker">
            <button
              className={`button${view == CalendarViewTypes.Monthly ? ' primary' : ''}`}
              onClick={() => setView(CalendarViewTypes.Monthly)}
              title={intl.formatMessage({ id: 'monthly' })}
            >
              <MdCalendarMonth />
            </button>
            <button
              className={`button${view == CalendarViewTypes.MonthAhead ? ' primary' : ''}`}
              onClick={() => setView(CalendarViewTypes.MonthAhead)}
              title={intl.formatMessage({ id: 'monthAhead' })}
            >
              <MdCalendarViewMonth />
            </button>
          </div>
        </span>
      </div>
      <div className="weekdays">
        {weekDays.map((weekday: string, i: number) => (
          <span key={i}>{weekday}</span>
        ))}
      </div>
      <div
        className="days"
        onPointerMove={(event: React.PointerEvent) => {
          if (!canDragSelect(event) || !isDraggingRef.current) return;
          const pointTarget = document.elementFromPoint(event.clientX, event.clientY);
          const dayEl = pointTarget?.closest('.day') as HTMLElement | null;
          const dateKey = dayEl?.dataset?.date;
          if (!dateKey || !dragStartRef.current) return;
          const current = toDay(new Date(dateKey));
          const last = lastDragDateRef.current;
          if (last && isSameDay(last, current)) return;
          lastDragDateRef.current = current;
          if (isSameDay(current, dragStartRef.current)) {
            didDragRef.current = true;
            onSelectRange?.([dragStartRef.current]);
            return;
          }
          didDragRef.current = true;
          onSelectRange?.(getDateRange(dragStartRef.current, current));
        }}
      >
        {calendarDays.map((day: CalendarDayData, i: number) => {
          const dayKey = toDay(day.date).toISOString();
          return (
            <CalendarDay
              date={day.date}
              dateKey={dayKey}
              events={day.events}
              renderEvent={renderEvent}
              isToday={isSameDay(day.date, convertTZ(new Date(), timezone))}
              isSelected={isSelectedDay(day.date)}
              onClick={() => {
                if (suppressClickRef.current) return;
                if (onSelectDate) {
                  onSelectDate(day.date);
                } else {
                  setSelectedDate(day.date);
                }
              }}
              onPointerDown={(event: React.PointerEvent) => {
                if (!canDragSelect(event)) return;
                event.preventDefault();
                isDraggingRef.current = true;
                didDragRef.current = false;
                dragStartRef.current = toDay(day.date);
                lastDragDateRef.current = toDay(day.date);
                setIsDragging(true);
              }}
              onPointerUp={(event: React.PointerEvent) => {
                if (!canDragSelect(event)) return;
                if (!isDraggingRef.current) return;
                suppressClickRef.current = didDragRef.current;
                isDraggingRef.current = false;
                dragStartRef.current = null;
                didDragRef.current = false;
                lastDragDateRef.current = null;
                setIsDragging(false);
                if (suppressClickRef.current) {
                  setTimeout(() => {
                    suppressClickRef.current = false;
                  }, 0);
                }
              }}
              key={i}
            />
          );
        })}
      </div>
    </div>
  );
};
